<script lang="ts" setup>
import { useRoute, useRouter } from 'vue-router';
import { AppTypography } from '~/components/base';
import type { BreadcrumbItem } from '~/types/components/base/Breadcrumb';
import ROUTE_LABELS from '~/utils/enums/RouteLabels';

const {
  customBreadcrumbs = [],
  includeHome = true,
  backFallback,
} = defineProps<{
  customBreadcrumbs?: BreadcrumbItem[];
  includeHome?: boolean;
  /**
   * Deep-link fallback target for the back arrow (used only when there's no
   * real history to pop — see `goBack` below). Overrides the default of
   * "the nearest linked crumb", for callers where that crumb is a section
   * list rather than the actual page the user would expect back navigation
   * to land on (e.g. /report-a-problem returning to the reported entity's
   * own Details page rather than its section list — VTW-495).
   */
  backFallback?: string;
}>();

const route = useRoute();
const router = useRouter();

const routes = computed((): BreadcrumbItem[] => {
  if (customBreadcrumbs.length > 0) {
    return customBreadcrumbs.map((breadcrumb, index, arr) => ({
      label: breadcrumb.label,
      to: index < arr.length - 1 ? breadcrumb.to : undefined,
    }));
  }

  const pathSegments = route.path.split('/').filter(Boolean);
  const breadcrumbs = [];

  if (route.path !== '/' && includeHome) {
    breadcrumbs.push({
      label: 'Home',
      to: '/',
    });
  }

  let currentPath = '';
  pathSegments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    const routeConfig = ROUTE_LABELS[currentPath];
    const label = routeConfig?.label || segment;
    const isHidden = routeConfig?.isHidden || false;
    const isLast = index === pathSegments.length - 1;

    if (!isHidden) {
      breadcrumbs.push({
        label,
        to: isLast ? undefined : currentPath,
      });
    }
  });

  return breadcrumbs;
});

// Popping real history is what preserves the previous screen's query and
// filters (e.g. returning to /search?q=… instead of a bare section list).
// A deep link has nothing to pop, though — going back would leave the site,
// so fall back to the nearest linked crumb.
//
// `window.history.state?.back` looks like the natural "is there history"
// check, but it lies after a hard navigation: some links (e.g.
// CampaignMiniCard on mobile) are plain <a> tags that do a full page load
// rather than an SPA push, and Vue Router re-initialises its history state
// on that load with `back: null` — even though the browser's real history
// stack still has the previous page. `history.length > 1` survives that
// case since it reflects the browser's actual stack, not Vue Router's — but
// it's still just a proxy for "is there a previous entry", not "is it the
// right one". A caller-supplied `backFallback` (report-a-problem's scoped
// entity URL) means the caller already knows the one correct destination.
//
// VTW-497: honoring it with a *push* was what caused a loop. The pushed
// entry left the report page still sitting in the stack, so the detail
// page's own Back — a plain `router.back()`, since it passes no fallback —
// returned to the report, and the two screens bounced forever. Compare the
// previous entry against the fallback instead:
//   - it matches      → pop it, so no duplicate entry is created and the
//                       previous screen keeps its scroll position and query
//   - it doesn't      → this is the cold-load case, so *replace* rather than
//                       push: the report entry must not survive for the
//                       detail page's Back to return to.
const goBack = () => {
  if (backFallback) {
    const previous = import.meta.client
      ? window.history.state?.back
      : undefined;
    // Strip the query/hash from the previous entry but NOT from
    // `backFallback`. The asymmetry is deliberate, and load-bearing:
    //
    //  - a bare `backFallback` (an entity Details page) should still match a
    //    previous entry carrying filters, so popping restores them
    //  - a `backFallback` that *does* carry a query is asking for that exact
    //    URL. `/campaigns?campaign=x` reopens a campaign popup over the
    //    listing; popping to a bare `/campaigns` would land on the same route
    //    with the popup closed, which is not where the user was.
    //
    // So stripping both sides would silently defeat any query-carrying
    // fallback. Locked in by a test.
    const previousPath =
      typeof previous === 'string' ? previous.split(/[?#]/)[0] : null;
    if (previousPath === backFallback) {
      router.back();
      return;
    }
    router.replace(backFallback);
    return;
  }
  if (import.meta.client && window.history.length <= 1) {
    const fallback = routes.value.findLast((crumb) => crumb.to)?.to;
    if (fallback) {
      router.push(fallback);
      return;
    }
  }
  router.back();
};

defineExpose({
  routes,
});
</script>

<template>
  <div class="app-breadcrumbs flex w-full items-center gap-2 p-2">
    <Icon
      name="solar:arrow-left-linear"
      size="24"
      class="cursor-pointer"
      @click="goBack()"
    />
    <div class="flex items-center justify-center gap-2">
      <template v-for="(breadcrumb, index) in routes" :key="index">
        <NuxtLink
          v-if="breadcrumb.to"
          :to="breadcrumb.to"
          class="font-subheadline-bold"
        >
          {{ breadcrumb.label }}
        </NuxtLink>
        <AppTypography
          v-else
          type="subheadline-bold"
          class="app-breadcrumbs__current"
        >
          {{ breadcrumb.label }}
        </AppTypography>
        <Icon
          v-if="index < routes.length - 1"
          name="solar:alt-arrow-right-linear"
          size="24"
        />
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.app-breadcrumbs {
  // The app-wide .router-link-active rule tints any matched link with a
  // background. A breadcrumb linking to the current path (e.g. "Search" on
  // /search?q=…) always matches, so it would render as a highlighted block.
  // Breadcrumbs are navigation text, never a selected state — opt out here so
  // every consumer inherits the fix.
  a.router-link-active,
  a.router-link-exact-active {
    background-color: transparent;
  }

  // Figma: the trailing crumb is the warm grey #a39c92, not this app's cool
  // $text-color-tertiary (#828294).
  &__current {
    color: #a39c92;
  }
}
</style>
